import type { ProposalContentOperation } from "work/proposals/negotiation/proposal-collaboration-contract";
import { proposalFieldHasPendingChange } from "work/proposals/negotiation/proposal-field-pending-change";
import type { ProposalTimelineEntryView } from "work/proposals/negotiation/proposal-timeline";
import type ProposalField from "work/proposals/proposal-fields/proposal-field";

/**
 * The value a field holds in the viewer's unsaved work: what its last queued `FieldSet` writes, with
 * `value` undefined when that change clears the field. Undefined when nothing queued sets the field.
 */
export type ProposalFieldUnsavedValue = { value?: string | number | boolean };

/**
 * The entries of a plain scalar field's history whose value can be put back, oldest first.
 *
 * `view` is one field's history as `buildProposalTimelineView` orders it, oldest first. An entry
 * qualifies when it is a content change that carries a stored value (`nextValue`, never the
 * rendering of it) and that value differs from the field's current one. The current value is the
 * viewer's unsaved one when `unsaved` is given, and otherwise the newest content change's, so the
 * field's present value is never offered as a no-op first entry (#6874), and neither is any earlier
 * entry that holds the same value. An unsaved edit is not on the timeline yet, so once the field has
 * been cleared or changed locally, the newest saved value is one to put back (#7085).
 *
 * A grouped row — a fee-schedule save or a collection item's folded leaves — is not a scalar value
 * and is never offered here; those revert through the collection operations in
 * `proposal-collection-item-revert.ts`.
 */
export function proposalFieldRestoreCandidates(
  view: readonly ProposalTimelineEntryView[],
  unsaved?: ProposalFieldUnsavedValue,
): ProposalTimelineEntryView[] {
  const contentChanges = view.filter((entry) => entry.isContentChange);
  const newest = contentChanges.at(-1);
  if (!newest) return [];
  const currentValue = unsaved ? unsaved.value : newest.nextValue;

  return contentChanges.filter(
    (entry) =>
      entry.nextValue !== undefined &&
      entry.nextDisplayValue !== undefined &&
      !entry.feeScheduleChange &&
      !entry.changeDetails?.length &&
      entry.nextValue !== currentValue,
  );
}

/**
 * What the viewer's queued operations leave in a plain scalar field (see
 * {@link ProposalFieldUnsavedValue}), read from the last `FieldSet` that `proposalFieldHasPendingChange`
 * places on this field. Pass the commands a Save has sent ahead of the queue, so the newest one wins.
 */
export function proposalFieldUnsavedValue(
  operations: readonly ProposalContentOperation[] | undefined,
  field: ProposalField | null | undefined,
): ProposalFieldUnsavedValue | undefined {
  const fieldSets = (operations ?? []).filter(
    (operation) => operation.kind === "FieldSet" && proposalFieldHasPendingChange([operation], field),
  );
  const operation = fieldSets.at(-1);
  if (operation?.kind !== "FieldSet") return undefined;

  const { value } = operation;
  return typeof value === "string" || typeof value === "number" || typeof value === "boolean" ? { value } : {};
}

/** Why the viewer may not put a field's earlier value back, in the order a page checks for it. */
export type ProposalFieldRestoreLocks = {
  /** The viewer holds no right to edit this proposal. */
  cannotEdit?: boolean;
  /** The viewer approved their review, which locks their edits. */
  reviewCompleted?: boolean;
  /** The page is open to view the proposal, not to edit it. */
  isViewOnly?: boolean;
  /** A save or another request is in flight, and the page holds its controls until it lands. */
  isPaused?: boolean;
};

/**
 * What a field's read-only history says about why its values cannot be picked (#6874), or undefined
 * when none of the locks holds. The first lock that holds names the reason: a missing right outlasts
 * the others, so it is the one to state.
 */
export function proposalFieldRestoreLockReason(locks: Readonly<ProposalFieldRestoreLocks>): string | undefined {
  if (locks.cannotEdit) return "Read-only: you can't edit this proposal.";
  if (locks.reviewCompleted) return "Read-only: you completed your review of this proposal.";
  if (locks.isViewOnly) return "Read-only: this proposal is open to view, not to edit.";
  if (locks.isPaused) return "Read-only while the proposal saves.";
  return undefined;
}

/** What an archived proposal's field history says: nothing on an archived proposal changes. */
export const proposalArchivedFieldRestoreLockReason = "Read-only: this proposal is archived.";
